import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Img from '../Img'
import constants from './consts'
import classNames from 'classnames'
import { useAnalytics } from 'src/common/analytics'
import { APPLY_FDR_URL } from 'src/common/constants'
import TrustpilotDesign from '../TrustpilotDesign'
import { useLeadId } from 'src/common/lead-id'
import { getCookies } from 'src/common/helpers'
import { useRouter } from 'next/router'
import { useTrustMarkers } from 'src/common/trust-markers'
import { TrustPilot } from 'src/components/shared/TrustMarkerWidgets'
import { numberFormat } from 'src/common/utils'

const Indicator = ({ label, value, className = '' }) => (
  <div className={`w-1/3 px-4 lg:mt-4 lg:w-1/3 ${className}`}>
    <div className="pb-3 lg:w-auto lg:pb-0">
      <div className="flex flex-col">
        <div className="w-20 whitespace-normal text-center text-14 text-black-base">
          {label}
        </div>
        <div className="whitespace-nowrap pt-2 text-center text-16 font-medium text-black-base">
          {value}
        </div>
      </div>
    </div>
  </div>
)

const CustomerConceptLeadCTA = ({
  className,
  trustMarkersCombination = null,
  variation = null,
  title = null,
}) => {
  const { track, utm_source } = useAnalytics()
  const url = `${APPLY_FDR_URL()}`
  const cookieLeadId = useLeadId()
  const [cookies] = useState(getCookies())
  const { query: baseQuery } = useRouter()
  const { getTrustMarkerById } = useTrustMarkers()
  const [mapping, setMapping] = useState(constants)
  const [trustPilotItem, setTrustPilotItem] = useState({})
  const trustPilot = getTrustMarkerById('truspilot')

  useEffect(() => {
    const trustPilotInfo = trustMarkersCombination?.trustMarker?.find(
      ({ fields }) =>
        fields.id.toLowerCase() === 'truspilot' ||
        fields.id.toLowerCase() === 'trustpilot'
    )
    if (trustPilotInfo) {
      setTrustPilotItem({
        rating: Number(trustPilotInfo?.fields?.rating),
        totalReviews: numberFormat(Number(trustPilotInfo?.fields?.totalReview)),
        link: trustPilotInfo?.fields?.link,
        color: '#33b083',
      })
    }
  }, [trustMarkersCombination])

  const handleClick = useCallback(() => {
    const data = {
      nav_link_section: 'Customer_centered',
      page_section: 'page body',
      click_type: 'Button Click',
      click_id: 'Get Started Lead CTA',
      click_url: url,
      click_text: `FDR-Web | Get Started`,
      track_event: 'button_click',
    }
    track(data, {}, 'click')
  }, [track, url])

  const handlePreviewClick = () => {
    track(
      {
        event_type: 'track',
        click_id: 'Trustpilot',
        click_type: 'Link Click',
        additional_info: {
          section: 'trust-marker-truspilot',
          click_url: trustPilotItem?.link || '',
        },
      },
      {},
      'button_click'
    )
  }

  const query = useMemo(() => {
    const urlParams = {
      from: 'fdr',
      utm_source,
      ...baseQuery,
      leadId: cookieLeadId,
      visit_id: cookies?.eh_visit_id || null,
    }

    for (let key in urlParams) {
      if (!urlParams[key]) {
        delete urlParams[key]
      }
    }

    const q = new URLSearchParams(urlParams)

    return q.toString()
  }, [baseQuery, cookieLeadId, cookies?.eh_visit_id, utm_source])

  useEffect(() => {
    const activeVariation =
      variation && constants[variation] ? constants[variation] : constants
    if (activeVariation) {
      setMapping(activeVariation)
    }
  }, [variation])

  return (
    <div
      data-testid="customer-concept-lead-cta"
      className={classNames(
        'mx-auto max-w-[708px] rounded-lg border border-gray-145 p-[32px]',
        className
      )}
    >
      <div className="flex flex-col">
        <h3 className="px-6 text-center text-[18px] font-bold leading-normal text-black-base md:px-0 md:text-[24px]">
          {title || constants.title}
        </h3>
        <div className="flex flex-col items-center py-4 pt-10 md:flex-row md:gap-6">
          <div className="relative flex flex-col items-center md:w-1/3">
            <Img
              priority={false}
              src={mapping.image}
              objectFit="cover"
              alt="Man smiling because he found debt relief"
              imgClassName={mapping.imageClassName}
              isBackgroundImage={false}
              className="h-[201px] w-[203px] rounded-full"
              styles={{ position: 'relative', height: '250px', width: 'auto' }}
            />
            <div
              className={classNames(
                !mapping.infoText
                  ? `right-[-20px] bottom-[30px]`
                  : 'right-0 bottom-[60px] md:right-[-20px]',
                'absolute  w-fit rounded-r-xl rounded-bl-xl bg-blue-140 py-1 pl-2 pr-4 text-12 text-blue-base-white md:bottom-[80px]'
              )}
            >
              <p className="m-0 text-[12px]">{mapping.caption}</p>
            </div>
            {mapping.infoText ? (
              <div
                className={`py-3 text-center text-[14px] italic text-neutral-300`}
              >
                {mapping.infoText}
              </div>
            ) : (
              <div
                className={`py-2 text-center text-[14px] italic text-neutral-300 md:py-3`}
              >
                <span className="hidden md:block">
                  Individual results are not typical and will vary.
                </span>
              </div>
            )}
          </div>
          <div className="mx-5 rounded-lg bg-blue-135 p-1 md:mx-0 md:w-2/3">
            <p className="p-2 text-center text-[16px]">{mapping.quote}</p>
            <div
              className={`my-4 flex w-full items-center justify-center divide-x-2 md:my-0 md:py-2`}
            >
              {mapping.clientStories.map((story, index) => (
                <Indicator
                  key={`client-story-${index}`}
                  label={story.title}
                  value={story.value}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="m-auto flex w-full flex-col justify-center px-4 md:w-3/5 md:px-0">
          <a
            className="mx-auto w-full cursor-pointer whitespace-nowrap rounded-lg bg-blue-base px-9 py-2.5 text-center text-lg font-semibold text-white md:w-4/5"
            onClick={handleClick}
            id="Continue_Lead_CTA"
            href={`${url}?${query}`}
          >
            {constants.leadCTA.button}
          </a>
          <div
            className="mt-1 flex flex-col items-center justify-center"
            data-testid="Trustpilot-hero-widget"
          >
            {trustMarkersCombination ? (
              <TrustPilot
                {...trustPilotItem}
                onReviewClick={handlePreviewClick}
              />
            ) : (
              <TrustpilotDesign
                starClassName="w-7 h-5.5"
                trustScoreClassName="text-lg"
                trustpilotClassName="text-lg"
                trustpilotRatingClassName="text-lg"
                starsGroupClassName="w-20 h-5 mx-2.5"
                className="my-2"
                starsGroupImgClassName="pt-1"
                truspilotReviews={trustPilot.totalReview || truspilotReviews}
                truspilotScore={Number(trustPilot.rating || truspilotScore)}
                isHome
                shortText
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomerConceptLeadCTA
